import { isNil, sum } from 'lodash'


export const P103_2B_CODES = {
    MAIN: 'P103.2B',
    NETWORK_LENGTH: 'VP.077',
    SUBTOTAL_A: 'VP.263',
    SUBTOTAL_AB: 'VP.264',
    SUBTOTAL_C: 'INT.P103.2B.C',
    CONDITION: 'VP.240',
    MATERIALS_RATE: 'VP.239',
    LAYING_DATE_RATE: 'VP.241',
}

const BOOL_POINTS = {
    'VP.236': 10,
    'VP.237': 5,
    'VP.238': 10,
    'VP.242': 10,
    'VP.243': 10,
    'VP.244': 10,
    'VP.245': 10,
    'VP.246': 10,
    'VP.247': 10,
    'VP.248': 10,
    'VP.249': 5,
}

export const P103_2B_PARTS = {
    A: ['VP.236', 'VP.237', P103_2B_CODES.SUBTOTAL_A],
    B: ['VP.238', P103_2B_CODES.CONDITION, P103_2B_CODES.MATERIALS_RATE, P103_2B_CODES.LAYING_DATE_RATE, P103_2B_CODES.SUBTOTAL_AB],
    C: ['VP.242', 'VP.243', 'VP.244', 'VP.245', 'VP.246', 'VP.247', 'VP.248', 'VP.249', P103_2B_CODES.SUBTOTAL_C],
}

const B_INPUTS = ['VP.238', P103_2B_CODES.CONDITION, P103_2B_CODES.MATERIALS_RATE, P103_2B_CODES.LAYING_DATE_RATE]
const C_INPUTS = ['VP.242', 'VP.243', 'VP.244', 'VP.245', 'VP.246', 'VP.247', 'VP.248', 'VP.249']

const B_THRESHOLD = 15
const C_THRESHOLD = 40

const isYes = value => `${value}` === 'true'
const parseRate = value => parseFloat(`${value ?? ''}`.replace(',', '.'))

const getRatePoints = (rate, base) => {
    if (isNaN(rate) || rate < 50) return 0
    if (rate >= 95) return base + 5
    return base + Math.floor((rate - 50) / 10)
}

const getMaxPoints = code => {
    if (code === P103_2B_CODES.MATERIALS_RATE) return 5
    if (code === P103_2B_CODES.LAYING_DATE_RATE) return 15
    return BOOL_POINTS[code]
}

export const getP103_2BPoints = (code, values) => {
    if (!isNil(BOOL_POINTS[code])) return isYes(values[code]) ? BOOL_POINTS[code] : 0
    const condition = isYes(values[P103_2B_CODES.CONDITION])
    if (code === P103_2B_CODES.MATERIALS_RATE) return condition ? getRatePoints(parseRate(values[code]), 0) : 0
    if (code === P103_2B_CODES.LAYING_DATE_RATE) return condition ? getRatePoints(parseRate(values[code]), 10) : 0
    return undefined
}

export const getP103_2BPointsLabel = (code, values) => {
    const points = getP103_2BPoints(code, values)
    return isNil(points) ? '' : `${points} / ${getMaxPoints(code)}`
}

const sumPoints = (codes, values) => sum(codes.map(code => getP103_2BPoints(code, values) ?? 0))

export const getP103_2BLocks = values => {
    const partA = sumPoints(['VP.236', 'VP.237'], values)
    const partB = sumPoints(['VP.238', P103_2B_CODES.MATERIALS_RATE, P103_2B_CODES.LAYING_DATE_RATE], values)
    const lockedB = partA < B_THRESHOLD
    return { lockedB, lockedC: lockedB || partA + partB < C_THRESHOLD }
}

const getLockedDefault = code => (code === P103_2B_CODES.MATERIALS_RATE || code === P103_2B_CODES.LAYING_DATE_RATE ? '0' : 'false')

const applyPartLock = (values, codes, locked, wasLocked) => {
    if (locked) return { ...values, ...Object.fromEntries(codes.map(code => [code, getLockedDefault(code)])) }
    if (wasLocked) return { ...values, ...Object.fromEntries(codes.map(code => [code, ''])) }
    return values
}

export const computeP103_2BValues = (values, previousValues) => {
    const previousLocks = getP103_2BLocks(previousValues)
    const withB = applyPartLock(values, B_INPUTS, getP103_2BLocks(values).lockedB, previousLocks.lockedB)
    const withC = applyPartLock(withB, C_INPUTS, getP103_2BLocks(withB).lockedC, previousLocks.lockedC)

    const partA = sumPoints(['VP.236', 'VP.237'], withC)
    const partB = sumPoints(['VP.238', P103_2B_CODES.MATERIALS_RATE, P103_2B_CODES.LAYING_DATE_RATE], withC)
    const partC = sumPoints(C_INPUTS, withC)
    return {
        ...withC,
        [P103_2B_CODES.SUBTOTAL_A]: `${partA}`,
        [P103_2B_CODES.SUBTOTAL_AB]: `${partA + partB}`,
        [P103_2B_CODES.SUBTOTAL_C]: `${partC}`,
        [P103_2B_CODES.MAIN]: `${partA + partB + partC}`,
    }
}
